import React, { useContext, useEffect, useState } from 'react';
import qs from 'qs';
import { YoutubeIframeApiContext } from 'src/contexts/YoutubeIframeApiProvider';

let ytApiPromise: Promise<void> | null = null;
let globalReady = false;

function useYTApiReady() {
  const { setIsReady } = useContext(YoutubeIframeApiContext)

  useEffect(() => {
    if (globalReady) return;
    if (!ytApiPromise) {
      ytApiPromise = new Promise((resolve) => {
        if (window.YT) {
          globalReady = true;
          setIsReady(true)
          resolve();
          return;
        }
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(tag);

        window.onYouTubeIframeAPIReady = () => {
          globalReady = true;
          setIsReady(true)
          resolve();
        };
      });
    }
    ytApiPromise.then(() => setIsReady(true));
  }, []);
}

export type Props = {
  youtubeId: string;
  controls?: number;
  autoplay?: number;
  muted?: number;
  loop?: number;
};

export const YOUTUBE_DEFAULT_PARAMS = {
  controls: 0,
  autoplay: 0,
  mute: 1,
  loop: 1,
  rel: 0,
  modestbranding: 1,
  enablejsapi: 1,
  disablekb: 1,
  fs: 0,
};

const EmbeddedYoutube = React.forwardRef<HTMLIFrameElement | null, React.PropsWithChildren<Props>>(
  ({ youtubeId, children, muted, ...rest }, ref) => {
    useYTApiReady()
    const loopValue = rest.loop ?? YOUTUBE_DEFAULT_PARAMS.loop;
    const youtubeParams = {
      ...YOUTUBE_DEFAULT_PARAMS,
      ...rest,
      loop: loopValue,
      ...(loopValue === 1 ? { playlist: youtubeId } : {}),
      mute: muted ?? YOUTUBE_DEFAULT_PARAMS.mute,
    };

    return (
      <iframe
        ref={ref}
        data-provider="youtube"
        data-videoid={youtubeId}
        className="js-video-embed"
        src={`https://www.youtube.com/embed/${youtubeId}?${qs.stringify(youtubeParams)}`}
        title="YouTube video player"
        frameBorder={0}
        referrerPolicy="strict-origin-when-cross-origin"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
      ></iframe>
    );
  }
);

EmbeddedYoutube.displayName = 'EmbeddedYoutube';

export default EmbeddedYoutube;
